import { DOCUMENT, Location } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  Renderer2,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBarRef } from '@angular/material/snack-bar';
import { RouterLink } from '@angular/router';
import { WINDOW } from '@ng-web-apis/common';
import { I18NEXT_SERVICE, I18NextModule, ITranslationService } from 'angular-i18next';
import { RecaptchaV3Module } from 'ng-recaptcha';
import { from, mergeMap, Observable } from 'rxjs';
import { FormScrollDirective } from 'src/app/common/form-scroll/form-scroll.directive';
import { GtagService } from 'src/app/common/gtag/gtag.service';
import { IconComponent } from 'src/app/common/icon/icon.component';
import { METADATA_SERVICE, MetadataService } from 'src/app/common/metadata/metadata.service';
import { OpenSnackBarService } from 'src/app/common/open-snack-bar/open-snack-bar.service';
import { UserService } from 'src/app/common/user/user.service';
import { getErrorMessageFromData, mailTelTo } from 'src/app/common/utils/helpers';
import { ContactsRequest, ContactsRole, User } from 'src/types/graphql';
import { FooterComponent } from '../../_shared/components/footer/footer.component';
import { HeaderComponent } from '../../_shared/components/header/header.component';
import { NotifyContactsRequestGQL } from './contacts.mutation.generated';
import { RouteTranslatePipe } from '../../_shared/pipes/route-translate.pipe';

@Component({
  selector: 'sharry-contacts',
  templateUrl: './contacts.component.html',
  styleUrls: ['./contacts.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    RouterLink,
    ReactiveFormsModule,
    MatButtonModule,
    MatSelectModule,
    MatFormFieldModule,
    MatInputModule,
    I18NextModule,
    IconComponent,
    RecaptchaV3Module,
    MatCheckboxModule,
    FormScrollDirective,
    HeaderComponent,
    FooterComponent,
    RouteTranslatePipe,
  ],
})
export class ContactsComponent implements OnInit, OnDestroy {
  public contactsForm: FormGroup<{
    name: FormControl<string>;
    surname: FormControl<string>;
    email: FormControl<string>;
    role: FormControl<ContactsRole>;
    request: FormControl<ContactsRequest>;
    message: FormControl<string>;
    businessName: FormControl<string>;
    city: FormControl<string>;
    phone: FormControl<string>;
    privacy: FormControl<boolean>;
  }>;
  public requestOptions: { [key: string]: string }[];
  public roleOptions: { [key: string]: string }[];
  public contactsRequestOthers = ContactsRequest.Others;
  public contactsRoleOthers = ContactsRole.Other;
  public user$: Observable<User>;
  snackBarRef: MatSnackBarRef<any>;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    @Inject(WINDOW) private window: Window,
    private renderer: Renderer2,
    private fb: FormBuilder,
    private notifyContactsRequestGQL: NotifyContactsRequestGQL,
    private userService: UserService,
    private cd: ChangeDetectorRef,
    private openSnackBarService: OpenSnackBarService,
    @Inject(I18NEXT_SERVICE) private translateService: ITranslationService,
    @Inject(METADATA_SERVICE) private metadataService: MetadataService,
    private location: Location,
    private gtagService: GtagService,
  ) {
    this.contactsForm = this.fb.group({
      name: this.fb.control<string>('', [Validators.required]),
      surname: this.fb.control<string>('', [Validators.required]),
      email: this.fb.control<string>('', [Validators.required]),
      role: this.fb.control<ContactsRole>(null, [Validators.required]),
      request: this.fb.control<ContactsRequest>(null, [Validators.required]),
      message: this.fb.control<string>('', [Validators.required]),
      businessName: this.fb.control<string>('', [Validators.nullValidator]),
      city: this.fb.control<string>('', [Validators.nullValidator]),
      phone: this.fb.control<string>('', [Validators.nullValidator]),
      privacy: this.fb.control<boolean>(false, [Validators.requiredTrue]),
    });
    this.requestOptions = Object.values(ContactsRequest)
      .filter(
        c => c !== ContactsRequest.Others && c !== ContactsRequest.EmailChange && c !== ContactsRequest.CanReportEvents,
      )
      .map(c => ({ key: c, value: this.translateService.t('contacts.request.' + c) }))
      .sort((a, b) => a.value.charCodeAt(0) - b.value.charCodeAt(0));
    this.roleOptions = Object.values(ContactsRole)
      .filter(c => c !== ContactsRole.Other)
      .map(c => ({ key: c, value: this.translateService.t('contacts.role.' + c) }))
      .sort((a, b) => a.value.charCodeAt(0) - b.value.charCodeAt(0));
  }

  ngOnInit(): void {
    this.metadataService.setMetadataPage('contacts', this.location.path());
    this.renderer.addClass(this.document.querySelector('html'), 'shrink');
    const user = this.userService.user;
    this.contactsForm.setValue({
      name: user?.name || '',
      surname: user?.surname || '',
      email: user?.email || '',
      businessName: user?.businessName || '',
      city: user?.city || '',
      phone: user?.contactPhone || '',
      role: null,
      request: null,
      message: '',
      privacy: false,
    });
    this.cd.detectChanges();
  }

  scrollToFrom() {
    this.document
      .querySelector('#form-section')
      .scrollIntoView({ inline: 'start', block: 'start', behavior: 'smooth' });
  }

  onMailto(subject: string) {
    mailTelTo(this.window, subject);
  }

  onTel(subject: string) {
    mailTelTo(this.window, subject.replace(/\s*/g, ''), 'tel');
  }

  onSubmit() {
    this.notifyContactsRequestGQL
      .mutate({
        input: {
          message: this.contactsForm.get('message').value,
          name: this.contactsForm.get('name').value,
          request: this.contactsForm.get('request').value,
          role: this.contactsForm.get('role').value,
          surname: this.contactsForm.get('surname').value,
          businessName: this.contactsForm.get('businessName').value,
          city: this.contactsForm.get('city').value,
          phone: this.contactsForm.get('phone').value,
          email: this.contactsForm.get('email').value,
        },
      })
      .pipe(mergeMap(() => from(import('src/app/_shared/components/generic-snack-bar/generic-snack-bar.component'))))
      .subscribe({
        next: ({ GenericSnackBarComponent }) => {
          this.snackBarRef = this.openSnackBarService.openSnackBar(GenericSnackBarComponent, {
            data: { text: 'snack-bar.contacts.message-sent-successful', close: true },
            duration: 3000,
          });
          this.contactsForm.reset();
          this.contactsForm.markAsPristine();
          this.contactsForm.get('message').markAsPristine();
          this.contactsForm.get('name').markAsPristine();
          this.contactsForm.get('request').markAsPristine();
          this.contactsForm.get('role').markAsPristine();
          this.contactsForm.get('surname').markAsPristine();
          this.contactsForm.get('businessName').markAsPristine();
          this.contactsForm.get('city').markAsPristine();
          this.contactsForm.get('phone').markAsPristine();
          this.contactsForm.get('email').markAsPristine();
          this.contactsForm.get('privacy').markAsPristine();
          this.gtagService.pushEvent({
            event: 'formSubmission',
            formID: 'Contacts Page Form',
          });
        },
        error: error => {
          if (this.snackBarRef) this.snackBarRef.dismiss();
          import('src/app/_shared/components/generic-snack-bar/generic-snack-bar.component').then(
            ({ GenericSnackBarComponent }) =>
              this.openSnackBarService.openSnackBar(GenericSnackBarComponent, {
                data: { text: getErrorMessageFromData(error, this.document), close: false },
                duration: 2000,
              }),
          );
        },
      });
  }

  ngOnDestroy() {
    this.metadataService.clean();
    this.renderer.removeClass(this.document.querySelector('html'), 'shrink');
  }
}
